import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsContent,
  MDBIcon,
  MDBAccordion,
  MDBAccordionItem,
  MDBSpinner
} from 'mdb-react-ui-kit';
import Summary from "./Tabs/Summary";
import DocumentsAndLinks from "./Tabs/DocumentsAndLinks";
import BillText from './Tabs/BillText';
import Amendments from "./Tabs/Amendments";
import Fiscal from "./Tabs/Fiscal";
import Status from "./Tabs/Status";

const tabMapping = {
  'Summary': 'sum',
  'BillText': 'bill',
  'Status': 'status',
  'Fiscal': 'fiscal',
  'Amendments': 'amend',
  'DocumentsAndLinks': 'docs'
};

const tabToCollapseId = {
  'Summary': '0',
  'BillText': '1',
  'Status': '2',
  'Fiscal': '3',
  'Amendments': '4',
  'DocumentsAndLinks': '5'
};

const Tabs = ({ billDetails, activeSessionInfo, ordinal, legislatureOrdinal, defaultTab }) => {
  const [basicActive, setBasicActive] = useState('Summary');
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loadingTab, setLoadingTab] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeAccordionItem, setActiveAccordionItem] = useState('0');
  const [mountedTabs, setMountedTabs] = useState(new Set(['Summary']));

  useEffect(() => {
    const mappedTab = Object.keys(tabMapping).find(
        key => tabMapping[key] === defaultTab.toLowerCase()
    );
    if (mappedTab) {
      setBasicActive(mappedTab);
      setActiveAccordionItem(tabToCollapseId[mappedTab]);
      setMountedTabs(prev => new Set([...prev, mappedTab]));
      updateQueryParam(tabMapping[mappedTab]);
    }
  }, [defaultTab]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && initialLoad && basicActive) {
      setLoadingTab(basicActive);
      const timer = setTimeout(() => {
        setLoadingTab(null);
        setInitialLoad(false);
        // Force a reflow after loading state is removed
        requestAnimationFrame(() => {
          const accordionItem = document.querySelector(`[data-mdb-target="#collapse${tabToCollapseId[basicActive]}"]`);
          if (accordionItem) {
            const content = accordionItem.nextElementSibling;
            if (content) {
              content.offsetHeight;
              content.style.height = 'auto';
            }
          }
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isMobile, basicActive, initialLoad]);

  useEffect(() => {
    if (loadingTab) {
      const timer = setTimeout(() => {
        setLoadingTab(null);
        setMountedTabs(prev => new Set([...prev, loadingTab]));
        // Force a reflow after loading state is removed
        requestAnimationFrame(() => {
          const accordionItem = document.querySelector(`[data-mdb-target="#collapse${tabToCollapseId[loadingTab]}"]`);
          if (accordionItem) {
            const content = accordionItem.nextElementSibling;
            if (content) {
              content.style.height = 'auto';
              content.offsetHeight;
              content.style.height = 'auto';
            }
          }
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loadingTab]);

  const updateQueryParam = (tabValue) => {
    const params = new URLSearchParams(location.search);
    params.set('open_tab', tabValue);
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleBasicClick = (value) => {
    if (value === basicActive) {
      return;
    }
    setLoadingTab(value);
    setBasicActive(value);
    setActiveAccordionItem(tabToCollapseId[value]);
    setMountedTabs(prev => new Set([...prev, value]));
    updateQueryParam(tabMapping[value]);
  };

  const handleAccordionChange = (activeItem) => {
    setActiveAccordionItem(activeItem);
    const tabKey = Object.keys(tabToCollapseId).find(
        key => tabToCollapseId[key] === activeItem
    );
    if (tabKey) {
      handleBasicClick(tabKey);
      setTimeout(() => {
        const accordionItem = document.querySelector(`[data-mdb-target="#collapse${activeItem}"]`);
        if (accordionItem) {
          const content = accordionItem.nextElementSibling;
          if (content) {
            content.style.height = 'auto';
            content.offsetHeight;
          }
        }
      }, 100);
    }
  };

  useEffect(() => {
    if (activeAccordionItem) {
      setTimeout(() => {
        const accordionItem = document.querySelector(`[data-mdb-target="#collapse${activeAccordionItem}"]`);
        if (accordionItem) {
          const content = accordionItem.nextElementSibling;
          if (content) {
            content.style.height = 'auto';
            content.offsetHeight;
          }
        }
      }, 100);
    }
  }, [activeAccordionItem, basicActive]);

  const renderTabContent = (tabKey) => {
    const isActive = basicActive === tabKey;
    const isLoading = loadingTab === tabKey;
    const isMounted = mountedTabs.has(tabKey);

    if (!isMounted && !isLoading) {
      return null;
    }

    if (isLoading && isMobile && tabKey === 'BillText') {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ 
          minHeight: '200px',
          position: 'relative',
          zIndex: 1,
          width: '100%'
        }}>
          <MDBSpinner color='primary' />
        </div>
      );
    }

    const content = (() => {
      switch (tabKey) {
        case 'Summary':
          return (
            <Summary
              active={isActive}
              bill={billDetails}
              onClickOpenTab={handleBasicClick}
              ordinal={ordinal}
              legislatureOrdinal={legislatureOrdinal}
            />
          );
        case 'BillText':
          return (
            <BillText
              active={isActive}
              bill={billDetails}
              ordinal={ordinal}
              legislatureOrdinal={legislatureOrdinal}
            />
          );
        case 'Status':
          return (
            <Status
              active={isActive}
              activeSessionInfo={activeSessionInfo}
              bill={billDetails}
              ordinal={ordinal}
              legislatureOrdinal={legislatureOrdinal}
            />
          );
        case 'Fiscal':
          return (
            <Fiscal
              active={isActive}
              bill={billDetails}
              ordinal={ordinal}
              legislatureOrdinal={legislatureOrdinal}
            />
          );
        case 'Amendments':
          return (
            <Amendments
              active={isActive}
              bill={billDetails}
              ordinal={ordinal}
              legislatureOrdinal={legislatureOrdinal}
            />
          );
        case 'DocumentsAndLinks':
          return (
            <DocumentsAndLinks
              active={isActive}
              bill={billDetails}
              ordinal={ordinal}
              legislatureOrdinal={legislatureOrdinal}
            />
          );
        default:
          return null;
      }
    })();

    return content;
  };

  useEffect(() => {
    if (basicActive && !loadingTab) {
      const updateAccordionHeight = () => {
        const accordionItem = document.querySelector(`[data-mdb-target="#collapse${tabToCollapseId[basicActive]}"]`);
        if (accordionItem) {
          const content = accordionItem.nextElementSibling;
          if (content) {
            content.style.height = 'auto';
            content.offsetHeight;
            content.style.height = 'auto';
          }
        }
      };

      requestAnimationFrame(updateAccordionHeight);

      window.addEventListener('resize', updateAccordionHeight);

      return () => window.removeEventListener('resize', updateAccordionHeight);
    }
  }, [basicActive, loadingTab]);

  if (isMobile) {
    return (
      <div style={{ 
        position: 'relative',
        backgroundColor: 'white',
        zIndex: 1,
        width: '100%'
      }}>
        <MDBAccordion
            alwaysOpen={false}
            initialActive={activeAccordionItem}
            onChange={handleAccordionChange}
            style={{
              height: 'auto',
              overflow: 'visible',
              position: 'relative',
              width: '100%'
            }}
        >
          <MDBAccordionItem
              collapseId="0"
              headerTitle={
                <div onClick={() => handleBasicClick('Summary')}>
                  <MDBIcon fas icon="clipboard-list" /> Summary
                </div>
              }
              style={{ 
                height: 'auto',
                overflow: 'visible',
                position: 'relative',
                zIndex: 6,
                width: '100%'
              }}
          >
            <div style={{ 
              height: 'auto',
              overflow: 'visible',
              position: 'relative',
              backgroundColor: 'white',
              width: '100%'
            }}>
              {renderTabContent('Summary')}
            </div>
          </MDBAccordionItem>
          <MDBAccordionItem
              collapseId="1"
              headerTitle={
                <div onClick={() => handleBasicClick('BillText')}>
                  <MDBIcon far icon="file-alt" /> Bill Text
                </div>
              }
              style={{ 
                height: 'auto',
                overflow: 'visible',
                position: 'relative',
                zIndex: 5,
                width: '100%'
              }}
          >
            <div style={{ 
              height: 'auto',
              overflow: 'visible',
              position: 'relative',
              backgroundColor: 'white',
              width: '100%'
            }}>
              {renderTabContent('BillText')}
            </div>
          </MDBAccordionItem>
          <MDBAccordionItem
              collapseId="2"
              headerTitle={
                <div onClick={() => handleBasicClick('Status')}>
                  <MDBIcon far icon="check-circle" /> Status
                </div>
              }
              style={{ 
                height: 'auto',
                overflow: 'visible',
                position: 'relative',
                zIndex: 4,
                width: '100%'
              }}
          >
            <div style={{ 
              height: 'auto',
              overflow: 'visible',
              position: 'relative',
              backgroundColor: 'white',
              width: '100%'
            }}>
              {renderTabContent('Status')}
            </div>
          </MDBAccordionItem>
          <MDBAccordionItem
              collapseId="3"
              headerTitle={
                <div onClick={() => handleBasicClick('Fiscal')}>
                  <MDBIcon fas icon="dollar-sign" /> Fiscal
                </div>
              }
              style={{ 
                height: 'auto',
                overflow: 'visible',
                position: 'relative',
                zIndex: 3,
                width: '100%'
              }}
          >
            <div style={{ 
              height: 'auto',
              overflow: 'visible',
              position: 'relative',
              backgroundColor: 'white',
              width: '100%'
            }}>
              {renderTabContent('Fiscal')}
            </div>
          </MDBAccordionItem>
          <MDBAccordionItem
              collapseId="4"
              headerTitle={
                <div onClick={() => handleBasicClick('Amendments')}>
                  <MDBIcon fas icon="pen-nib" /> Amendments
                </div>
              }
              style={{ 
                height: 'auto',
                overflow: 'visible',
                position: 'relative',
                zIndex: 2,
                width: '100%'
              }}
          >
            <div style={{ 
              height: 'auto',
              overflow: 'visible',
              position: 'relative',
              backgroundColor: 'white',
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {renderTabContent('Amendments')}
            </div>
          </MDBAccordionItem>
          <MDBAccordionItem
              collapseId="5"
              headerTitle={
                <div onClick={() => handleBasicClick('DocumentsAndLinks')}>
                  <MDBIcon fas icon="paperclip" /> Documents &amp; Links
                </div>
              }
              style={{ 
                height: 'auto',
                overflow: 'visible',
                position: 'relative',
                zIndex: 1,
                width: '100%'
              }}
          >
            <div style={{ 
              height: 'auto',
              overflow: 'visible',
              position: 'relative',
              backgroundColor: 'white',
              width: '100%'
            }}>
              {renderTabContent('DocumentsAndLinks')}
            </div>
          </MDBAccordionItem>
        </MDBAccordion>
      </div>
    );
  }

  return (
      <div>
        <MDBTabs>
          <MDBTabsItem>
            <MDBTabsLink onClick={() => handleBasicClick('Summary')} active={basicActive === 'Summary'}>
              <MDBIcon fas icon="clipboard-list" /> Summary
            </MDBTabsLink>
          </MDBTabsItem>
          <MDBTabsItem>
            <MDBTabsLink onClick={() => handleBasicClick('BillText')} active={basicActive === 'BillText'}>
              <MDBIcon far icon="file-alt" /> Bill Text
            </MDBTabsLink>
          </MDBTabsItem>
          <MDBTabsItem>
            <MDBTabsLink onClick={() => handleBasicClick('Status')} active={basicActive === 'Status'}>
              <MDBIcon far icon="check-circle" /> Status
            </MDBTabsLink>
          </MDBTabsItem>
          <MDBTabsItem>
            <MDBTabsLink onClick={() => handleBasicClick('Fiscal')} active={basicActive === 'Fiscal'}>
              <MDBIcon fas icon="dollar-sign" /> Fiscal
            </MDBTabsLink>
          </MDBTabsItem>
          <MDBTabsItem>
            <MDBTabsLink onClick={() => handleBasicClick('Amendments')} active={basicActive === 'Amendments'}>
              <MDBIcon fas icon="pen-nib" /> Amendments
            </MDBTabsLink>
          </MDBTabsItem>
          <MDBTabsItem>
            <MDBTabsLink onClick={() => handleBasicClick('DocumentsAndLinks')} active={basicActive === 'DocumentsAndLinks'}>
              <MDBIcon fas icon="paperclip" /> Documents &amp; Links
            </MDBTabsLink>
          </MDBTabsItem>
        </MDBTabs>

        <MDBTabsContent className="bg-white p-3">
          {renderTabContent('Summary')}
          {renderTabContent('BillText')}
          {renderTabContent('Status')}
          {renderTabContent('Fiscal')}
          {renderTabContent('Amendments')}
          {renderTabContent('DocumentsAndLinks')}
        </MDBTabsContent>
      </div>
  );
};

export default Tabs;
